import { useState, useCallback } from 'react';

import { useRestApiHeaders } from 'business/contextProviders/useApiHeaders';
import { syncDefectReportData } from 'business/quality-control/pages/record-segment-defect/utils/sync-quality-data/sync-defect-report-data';
import { syncDefectReportPictureData } from 'business/quality-control/pages/record-segment-defect/utils/sync-quality-data/sync-defect-report-picture-data';
import { syncRepairReportData } from 'business/quality-control/pages/record-segment-defect/utils/sync-quality-data/sync-repair-report-data';
import { syncRepairReportPictureData } from 'business/quality-control/pages/record-segment-defect/utils/sync-quality-data/sync-repair-report-picture-data';
import { syncRingData } from 'business/quality-control/pages/record-segment-defect/utils/sync-quality-data/sync-ring-data';
import { Module_Enum } from 'generated/graphql';
import { utilsDbService } from 'technical/db/db.service';

export const useSyncQualityData = (
  constructionSiteId: string,
  ringNumberStart: number,
  ringNumberEnd: number,
) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const restHeaders = useRestApiHeaders(Module_Enum.QualityControl);

  const handleSync = useCallback(async () => {
    setIsSyncing(true);
    setError(null);

    try {
      await utilsDbService.resetData();

      await Promise.all([
        syncRingData(
          constructionSiteId,
          ringNumberStart,
          ringNumberEnd,
          restHeaders,
        ),
        syncDefectReportData(
          constructionSiteId,
          ringNumberStart,
          ringNumberEnd,
          restHeaders,
        ),
        syncRepairReportData(
          constructionSiteId,
          ringNumberStart,
          ringNumberEnd,
          restHeaders,
        ),
      ]);
      await Promise.all([
        syncDefectReportPictureData(
          constructionSiteId,
          ringNumberStart,
          ringNumberEnd,
          restHeaders,
        ),
        syncRepairReportPictureData(
          constructionSiteId,
          ringNumberStart,
          ringNumberEnd,
          restHeaders,
        ),
      ]);

      return { success: true };
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Technical error'));

      return { success: false };
    } finally {
      setIsSyncing(false);
    }
  }, [constructionSiteId, ringNumberStart, ringNumberEnd, restHeaders]);

  return {
    handleSync,
    isSyncing,
    error,
  };
};
